import { useEffect, useId, useState, type CSSProperties } from "react";
import {
  adminListIngredientImpacts,
  adminSetIngredientAvailability,
  type AdminIngredientFull,
  type IngredientImpactRecipe,
} from "../../lib/api";
import { useDialogA11y } from "../../lib/useDialogA11y";

interface Props {
  ingredient: AdminIngredientFull;
  goingTo: boolean; // true = setting available, false = setting unavailable
  /** From app_settings.auto_86_on_unavailable_ingredient. Drives whether the
   * cascade-86 checkbox starts checked (true) or unchecked (false). Manager
   * can override per toggle either way. Default false per Phase 10 polish. */
  autoCascadeDefault?: boolean;
  onClose: () => void;
  onConfirmed: (updatedAvailable: boolean, cascadeCount: number) => void;
}

/**
 * Confirmation modal shown when toggling ingredient availability. If we're
 * setting available=false, also fetches the affected approved recipes and
 * surfaces the cascade-86 option.
 */
export default function IngredientImpactModal({
  ingredient,
  goingTo,
  autoCascadeDefault = false,
  onClose,
  onConfirmed,
}: Props) {
  const [recipes, setRecipes] = useState<IngredientImpactRecipe[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cascade86, setCascade86] = useState(autoCascadeDefault);
  const titleId = useId();
  const { dialogRef } = useDialogA11y(true, onClose, !busy);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminListIngredientImpacts(ingredient.id);
        if (!cancelled) setRecipes(r.recipes);
      } catch (err: any) {
        if (!cancelled) setError(err?.message ?? "Could not load impacts");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ingredient.id]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await adminSetIngredientAvailability(
        ingredient.id,
        goingTo,
        !goingTo && cascade86,
      );
      onConfirmed(goingTo, res.cascade_drink_count ?? 0);
    } catch (err: any) {
      setError(err?.message ?? "Could not update");
      setBusy(false);
    }
  }

  return (
    <div style={styles.backdrop} onClick={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={styles.dialog}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} style={styles.title}>
          {goingTo ? "Mark available" : "Mark unavailable"}
        </h2>
        <p style={styles.body}>
          <strong>{ingredient.name}</strong> will become {goingTo ? "available" : "unavailable"}.
        </p>

        {!goingTo && (
          <>
            {loading && <p style={styles.muted}>Checking which recipes use this…</p>}
            {!loading && recipes && recipes.length === 0 && (
              <p style={styles.muted}>No live approved recipes use this ingredient.</p>
            )}
            {!loading && recipes && recipes.length > 0 && (
              <>
                <p style={styles.warningLine}>
                  <strong>{recipes.length}</strong> live approved {recipes.length === 1 ? "recipe" : "recipes"} {recipes.length === 1 ? "uses" : "use"} this ingredient:
                </p>
                <ul style={styles.recipeList}>
                  {recipes.map((r) => (
                    <li key={r.version_id} style={styles.recipeItem}>
                      <span style={styles.recipeName}>{r.name}</span>
                      <span style={styles.recipeMeta}>
                        v{r.version_number} · {r.service_status}
                      </span>
                    </li>
                  ))}
                </ul>
                <label style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={cascade86}
                    onChange={(e) => setCascade86(e.target.checked)}
                    disabled={busy}
                  />
                  <span>
                    <strong>Mark these {recipes.length === 1 ? "recipe" : "recipes"} 86&apos;d</strong> on Station Mode (recommended). Bartenders see them as 86&apos;d immediately. You can flip them back to Active later.
                  </span>
                </label>
              </>
            )}
          </>
        )}

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.actions}>
          <button type="button" onClick={onClose} disabled={busy} style={styles.cancelBtn}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || loading}
            style={goingTo ? styles.confirmBtnGreen : styles.confirmBtnRed}
          >
            {busy
              ? "Updating…"
              : goingTo
                ? "Mark available"
                : recipes && recipes.length > 0 && cascade86
                  ? `Mark unavailable & 86 ${recipes.length}`
                  : "Mark unavailable"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 99,
  },
  dialog: {
    width: "100%",
    maxWidth: 520,
    maxHeight: "90vh",
    overflowY: "auto",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: 20,
    boxShadow: "0 8px 28px rgba(0,0,0,0.5)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 22,
    margin: "0 0 8px",
    color: "var(--text)",
  },
  body: { margin: "0 0 12px", fontSize: 13, color: "var(--muted)", lineHeight: 1.5 },
  muted: { margin: "0 0 12px", fontSize: 13, color: "var(--muted)", fontStyle: "italic" },
  warningLine: {
    margin: "0 0 8px",
    padding: 10,
    borderRadius: 8,
    background: "rgba(249,175,54,0.08)",
    border: "1px solid rgba(249,175,54,0.3)",
    color: "var(--gold)",
    fontSize: 12,
    lineHeight: 1.4,
  },
  recipeList: { listStyle: "none", padding: 0, margin: "0 0 12px", display: "grid", gap: 4 },
  recipeItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 12,
  },
  recipeName: { color: "var(--text)", fontWeight: 600 },
  recipeMeta: {
    color: "var(--muted)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 10,
  },
  checkboxRow: {
    display: "flex",
    gap: 8,
    alignItems: "flex-start",
    fontSize: 12,
    color: "var(--text)",
    margin: "8px 0 0",
    lineHeight: 1.4,
  },
  error: {
    marginTop: 10,
    padding: 10,
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 10,
    fontSize: 12,
  },
  actions: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 16,
  },
  cancelBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  confirmBtnGreen: {
    background: "rgba(39,174,96,0.2)",
    color: "#86efac",
    border: "1px solid rgba(39,174,96,0.4)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  confirmBtnRed: {
    background: "rgba(224,80,64,0.2)",
    color: "#FF8A7A",
    border: "1px solid rgba(224,80,64,0.5)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
};
